import { DOCUMENT } from '@angular/common';
import { ApplicationRef, Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { IModelData } from '@df/catalog/model';
import { ADD_RESOURCE_EVENT, Resource } from '@df/catalog/resource';
import { App } from '@df/core/app';
import { NCore } from '@df/core/df-core.interfaces';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { OnInteractionService } from '@df/ui/common/on-interaction.service';
import each from 'lodash-es/each';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

const TRANSFERSCRIPT_ID = 'un-transfer-script';

interface IAssets {
    [key: string]: IModelData[];
}

@Injectable({
    providedIn: 'root'
})
export class UniversalTransferService {
    /**
     * Initial assets
     */
    private initData: IModelData[];

    /**
     * Waiting observers
     */
    private observers = [];

    /**
     * Transfer script element
     */
    private transferScript: HTMLScriptElement;

    /**
     * Renderer
     */
    private renderer2: Renderer2;

    /**
     * Subscription
     */
    transitionSubscription: Subscription;

    /**
     * Needs to be injected cause of cyclic deps
     */
    @NgInject(ApplicationRef)
    private applicationRef: ApplicationRef;

    /**
     * Debounced writeSsrData
     */
    // private writeSsrDataDebounced: () => void;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private rendererFactory: RendererFactory2,
        private app: App,
        events: Events,
        private timeoutService: TimeoutService,
        afterContentInit: AfterContentInitDirectiveService,
        private onInteractionService: OnInteractionService,
        private client: Client,
        @Inject(NCore.OPTIONS) private moduleConfig: NCore.IModuleOptions
    ) {
        // this interval is to make sure that app is not stable till we finish rendering
        this.renderer2 = this.rendererFactory.createRenderer(null, null);

        app.isPlatformBrowser
            ? this.renderer2.removeClass(this.document.body, 'cursor-wait')
            : this.renderer2.addClass(this.document.body, 'cursor-wait');
        if (app.isPlatformBrowser) {
            this.initData = window['__transferData']?.assets;

            const quickRunScript = this.document.getElementById('quickRunScript');
            if (quickRunScript) {
                quickRunScript.remove();
            }
            // see quick run script - we set there min height to prevent jump on rerender
            // this.timeoutService.setTimeout(() => {
            //     document.body.style.minHeight = '';
            // }, 1000);

            this.transitionSubscription = afterContentInit.loaded.subscribe(value => {
                if (value && !document.cookie.includes('swapoff=1')) {
                    this.runTransition();
                }
            });
        } else {
            if (this.client.sizeId > 1) {
                const sizeClassScript = this.renderer2.createElement('script');
                this.renderer2.setAttribute(sizeClassScript, 'id', 'desktopSizeClassScript');
                sizeClassScript.innerHTML = `
                (function desktopSizeClass() {
                        if (window.innerWidth >= ${this.moduleConfig.breakpoints.X.width} && document.body.classList.contains('size-l')) {
                            document.body.classList.remove('size-l');
                            document.body.classList.add('size-x');
                        } else if (window.innerWidth < ${this.moduleConfig.breakpoints.X.width} && document.body.classList.contains('size-x')) {
                            document.body.classList.remove('size-x');
                            document.body.classList.add('size-l');
                        }
                })();
            `;
                this.document.body.insertBefore(sizeClassScript, this.document.body.firstChild);
            }

            const script = this.renderer2.createElement('script');
            this.renderer2.setAttribute(script, 'type', 'application/json');
            this.renderer2.setAttribute(script, 'id', TRANSFERSCRIPT_ID);
            this.renderer2.appendChild(this.document.body, script);
            this.renderer2.addClass(this.document.body, 'is-un');

            this.transferScript = script;
            const intId = setInterval(() => {}, 500);
            afterContentInit.ready.subscribe(value => {
                if (value) {
                    this.writeSsrData();
                    clearInterval(intId);
                    // if in 500ms it does not cach that its stable then kick it with a hacky stable event
                    this.timeoutService.setTimeout(
                        () => {
                            const appRef = this.applicationRef as any;
                            const zone = this.timeoutService.ngZone as any;
                            appRef._stable = false;
                            zone.hasPendingMacrotasks = false;
                            zone.hasPendingMicrotasks = false;

                            zone.onStable.emit(null);
                        },
                        500,
                        false
                    );
                }
            });

            each(document.head.querySelectorAll('link'), link => {
                if (link.href && link.href.includes('styles.css?')) {
                    const href = link.getAttribute('href');
                    this.renderer2.setAttribute(link, 'unHref', href);
                    this.renderer2.setAttribute(link, 'href', href.replace(/\?.*/, ''));
                }
            });
        }
        each(app.resources, resource => this.registerResource(resource));
        events.on<Resource<any>>(ADD_RESOURCE_EVENT).subscribe(resource => this.registerResource(resource));
    }

    /**
     * Read from transfer or set up transfer listender
     */
    private registerResource(resource: Resource<any>) {
        if (this.app.isPlatformBrowser) {
            if (this.initData) {
                each(this.initData[resource.type] || [], asset => {
                    const model = resource.getCreate(asset.id);
                    model.setData(asset);
                });
            }
        }
    }

    /**
     * Bufer data in script
     */
    private writeSsrData() {
        const assets: IAssets = {};
        each(this.app.resources, resource => {
            // conditions is to support both legacy and current version of catalog module
            if (resource['canTransferSsrData'] || resource['invalidatesUniversal']) {
                const datas = resource
                    .getAllExisting()
                    .map(i => i.rawData)
                    .filter(i => i?.id) as IModelData[];
                if (datas.length) {
                    assets[resource.type] = datas;
                }
            }
        });
        try {
            // ESC_SCRIPT here is only to prevent from catching inner closing tags as actual closing tag of the script element (see extractModelsMapFromHtml)
            this.renderer2.setProperty(
                this.transferScript,
                'innerHTML',
                JSON.stringify({ assets }).replace(/<\/script>/gim, '</ESC_SCRIPT>')
            );
        } catch (err) {
            console.error(err);
        }
    }

    runTransition() {
        this.transitionSubscription?.unsubscribe();
        const unAppElem = document.getElementsByTagName('un-app')[0];
        this.renderer2.removeClass(this.document.body, 'is-un');

        // implementation of lazy loading for styles (using temp media print) is buggy, fails when importing asset that cannot be loaded
        // this is just a extra protection not to crash the app
        each(document.querySelectorAll('link[media="print"]'), (i: HTMLLinkElement) => {
            // eslint-disable-next-line @typescript-eslint/quotes
            if (i.getAttribute('onload') === "this.media='all'") {
                i.media = 'all';
            }
        });

        // we need to force repaint on app-root to make images appear
        // https://tomandco.atlassian.net/browse/MFR-788
        const isMacMobile = /iPhone|iPad/i.test(navigator.userAgent);
        if (isMacMobile) {
            const forceRepaintElement = document.body;
            if (forceRepaintElement) {
                forceRepaintElement.style.transform = 'scale(1)';
                this.timeoutService.setTimeout(() => {
                    forceRepaintElement.style.transform = '';
                }, 100);
                // double repaint to make sure that it works
                this.timeoutService.setTimeout(() => {
                    forceRepaintElement.style.transform = 'scale(1)';
                    this.timeoutService.setTimeout(() => {
                        forceRepaintElement.style.transform = '';
                    }, 100);
                }, 300);
            }
        }

        if (unAppElem) {
            each(document.querySelectorAll('link[un-app-link]'), i => i.remove());
            this.onInteractionService.anyInteraction
                .pipe(
                    filter<boolean>(visible => visible),
                    take(1)
                )
                .subscribe(() => {
                    unAppElem.remove();
                    each(document.querySelectorAll('style[un-app-style]'), i => i.remove());
                });
        }
    }
}
